import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Avatar,
  Chip,
  Alert,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupportAgent as SupportIcon,
  SupervisorAccount as SupervisorIcon,
  ManageAccounts as ManagerIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon
} from '@mui/icons-material';

import useAuth from '../../../hooks/useAuth';
import usersService from '../../../services/usersService';
import queuesService from '../../../services/queuesService';

// Definir tipos de usuário
const USER_TYPES = {
  ADMIN: 'admin',
  AGENT: 'agent',
  SUPERVISOR: 'supervisor',
  MANAGER: 'manager'
};

const USER_TYPE_LABELS = {
  [USER_TYPES.ADMIN]: 'Administrador',
  [USER_TYPES.AGENT]: 'Agente',
  [USER_TYPES.SUPERVISOR]: 'Supervisor',
  [USER_TYPES.MANAGER]: 'Gerência'
};

const USER_TYPE_ICONS = {
  [USER_TYPES.ADMIN]: <AdminIcon />,
  [USER_TYPES.AGENT]: <SupportIcon />,
  [USER_TYPES.SUPERVISOR]: <SupervisorIcon />,
  [USER_TYPES.MANAGER]: <ManagerIcon />
};

const Users = () => {
  const { user: currentUser } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [queues, setQueues] = useState([]);
  
  const [openModal, setOpenModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    phone: '',
    type: USER_TYPES.AGENT,
    active: true,
    password: '',
    queueIds: []
  });
  
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  }, []);

  const closeNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await usersService.getUsers();
      const usersData = Array.isArray(response) ? response : (response.data || []);
      setUsers(usersData);
    } catch (error) {
      showNotification('Erro ao carregar usuários', 'error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const loadQueues = useCallback(async () => {
    try {
      const response = await queuesService.getQueues();
      const queuesData = Array.isArray(response) ? response : (response.data || []);
      setQueues(queuesData);
    } catch (error) {
      setQueues([]);
    }
  }, []);

  useEffect(() => {
    const filtered = users.filter(user =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm)
    );
    setFilteredUsers(filtered);
  }, [users, searchTerm]);

  useEffect(() => {
    loadUsers();
    loadQueues();
  }, [loadUsers, loadQueues]);

  const openCreateModal = useCallback(() => {
    setEditingUser(null);
    setUserForm({
      name: '',
      email: '',
      phone: '',
      type: USER_TYPES.AGENT,
      active: true,
      password: '',
      queueIds: []
    });
    setOpenModal(true);
  }, []);

  const openEditModal = useCallback((user) => {
    setEditingUser(user);
    setUserForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      type: user.type || USER_TYPES.AGENT,
      active: user.active !== undefined ? user.active : true,
      password: '',
      queueIds: user.queues ? user.queues.map(q => q.id) : []
    });
    setOpenModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpenModal(false);
    setEditingUser(null);
  }, []);

  const handleSaveUser = useCallback(async () => {
    setLoading(true);
    try {
      if (editingUser) {
        await usersService.updateUser(editingUser.id, userForm);
        showNotification('Usuário atualizado com sucesso');
      } else {
        await usersService.createUser(userForm);
        showNotification('Usuário criado com sucesso');
      }
      closeModal();
      loadUsers();
    } catch (error) {
      showNotification(
        error.response?.data?.message || 'Erro ao salvar usuário',
        'error'
      );
      console.error('Erro ao salvar usuário:', error);
    } finally {
      setLoading(false);
    }
  }, [editingUser, userForm, loadUsers, showNotification, closeModal]);

  const handleDeleteUser = useCallback(async (userId) => {
    if (!window.confirm('Tem certeza que deseja remover este usuário?')) {
      return;
    }

    setLoading(true);
    try {
      await usersService.deleteUser(userId);
      showNotification('Usuário removido com sucesso');
      loadUsers();
    } catch (error) {
      showNotification('Erro ao remover usuário', 'error');
      console.error('Erro ao remover usuário:', error);
    } finally {
      setLoading(false);
    }
  }, [loadUsers, showNotification]);

  const handleToggleUserStatus = useCallback(async (userId) => {
    setLoading(true);
    try {
      await usersService.toggleUserStatus(userId);
      showNotification('Status do usuário alterado com sucesso');
      loadUsers();
    } catch (error) {
      showNotification('Erro ao alterar status do usuário', 'error');
      console.error('Erro ao alterar status:', error);
    } finally {
      setLoading(false);
    }
  }, [loadUsers, showNotification]);

  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const stats = {
    total: users.length,
    active: users.filter(u => u.active).length,
    admins: users.filter(u => u.type === USER_TYPES.ADMIN).length,
    agents: users.filter(u => u.type === USER_TYPES.AGENT).length,
    supervisors: users.filter(u => u.type === USER_TYPES.SUPERVISOR).length,
    managers: users.filter(u => u.type === USER_TYPES.MANAGER).length
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Gerenciamento de Usuários
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateModal}
          disabled={loading}
          sx={{ minWidth: 150 }}
        >
          Novo Usuário
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{stats.total}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Usuários
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <ToggleOnIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{stats.active}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Usuários Ativos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <AdminIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{stats.admins}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Administradores
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <SupportIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{stats.agents}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Agentes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Buscar por nome, email ou telefone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Usuário</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Filas</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Carregando usuários...' : 'Nenhum usuário encontrado'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {getInitials(user.name)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {user.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.phone || 'Não informado'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {USER_TYPE_ICONS[user.type]}
                      <Typography variant="body2">
                        {USER_TYPE_LABELS[user.type]}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {user.queues && user.queues.length > 0 ? (
                        user.queues.map((queue) => (
                          <Chip
                            key={queue.id}
                            label={queue.name}
                            size="small"
                            sx={{ 
                              bgcolor: queue.color || '#007bff',
                              color: 'white',
                              fontSize: '0.75rem'
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Nenhuma fila
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.active ? 'Ativo' : 'Inativo'}
                      color={user.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton
                        onClick={() => openEditModal(user)}
                        disabled={loading}
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={user.active ? 'Desativar' : 'Ativar'}>
                      <IconButton
                        onClick={() => handleToggleUserStatus(user.id)}
                        disabled={loading || user.id === currentUser?.id}
                        size="small"
                        color={user.active ? 'warning' : 'success'}
                      >
                        {user.active ? <ToggleOffIcon /> : <ToggleOnIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remover">
                      <IconButton
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={loading || user.id === currentUser?.id}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openModal} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Nome"
              value={userForm.name}
              onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={userForm.email}
              onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              label="Telefone"
              value={userForm.phone}
              onChange={(e) => setUserForm(prev => ({ ...prev, phone: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Tipo de Usuário</InputLabel>
              <Select
                value={userForm.type}
                onChange={(e) => setUserForm(prev => ({ ...prev, type: e.target.value }))}
                label="Tipo de Usuário"
              >
                {Object.entries(USER_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {USER_TYPE_ICONS[value]}
                      {label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Filas</InputLabel>
              <Select
                multiple
                value={userForm.queueIds}
                onChange={(e) => setUserForm(prev => ({ ...prev, queueIds: e.target.value }))}
                input={<OutlinedInput label="Filas" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((queueId) => {
                      const queue = queues.find(q => q.id === queueId);
                      return (
                        <Chip
                          key={queueId}
                          label={queue?.name || queueId}
                          size="small"
                          sx={{ 
                            bgcolor: queue?.color || '#007bff',
                            color: 'white'
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {queues.map((queue) => (
                  <MenuItem key={queue.id} value={queue.id}>
                    <Checkbox checked={userForm.queueIds.indexOf(queue.id) > -1} />
                    <ListItemText primary={queue.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {!editingUser && (
              <TextField
                fullWidth
                label="Senha"
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                required
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={userForm.active}
                  onChange={(e) => setUserForm(prev => ({ ...prev, active: e.target.checked }))}
                />
              }
              label="Usuário ativo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={loading || !userForm.name || !userForm.email || (!editingUser && !userForm.password)}
          >
            {editingUser ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
